import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Plus, Settings, Star } from 'lucide-react';
import { toast } from 'sonner';

export default function AcademicConfig() {
  const [years, setYears] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ name: '', start_date: '', end_date: '', is_current: false, terms: [] });
  const [termForm, setTermForm] = useState({ name: '', start_date: '', end_date: '' });

  const load = () => api.get('/admin/academic-years').then(r => setYears(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addTerm = () => {
    if (termForm.name) {
      setForm({ ...form, terms: [...form.terms, { ...termForm }] });
      setTermForm({ name: '', start_date: '', end_date: '' });
    }
  };

  const handleSave = async () => {
    try {
      await api.post('/admin/academic-years', form);
      toast.success('Academic year created');
      setShowDialog(false);
      setForm({ name: '', start_date: '', end_date: '', is_current: false, terms: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const setCurrent = async (id) => {
    await api.put(`/admin/academic-years/${id}`, { is_current: true });
    toast.success('Set as current academic year');
    load();
  };

  return (
    <div data-testid="academic-config">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-[#1B2A4A] flex items-center gap-2"><Settings className="w-6 h-6" /> Academic Configuration</h1>
        <Button onClick={() => setShowDialog(true)} className="bg-[#1B2A4A]" data-testid="add-academic-year-btn"><Plus className="w-4 h-4 mr-1" /> Add Academic Year</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {years.length === 0 ? (
          <Card><CardContent className="py-8 text-center text-slate-400">No academic years configured</CardContent></Card>
        ) : years.map(y => (
          <Card key={y.id} className={y.is_current ? 'border-2 border-[#C9A84C]' : ''}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{y.name}</CardTitle>
                {y.is_current ? <Badge className="bg-[#C9A84C] text-[#1B2A4A]"><Star className="w-3 h-3 mr-1" /> Current</Badge> :
                  <Button size="sm" variant="outline" onClick={() => setCurrent(y.id)}>Set Current</Button>}
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500">{y.start_date} to {y.end_date}</p>
              {y.terms?.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs font-bold text-slate-600 uppercase">Terms</p>
                  {y.terms.map((t, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg p-2 text-sm">
                      <span className="font-semibold">{t.name}</span>
                      <span className="text-slate-400 text-xs">{t.start_date} - {t.end_date}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Academic Year</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Year Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., 2026" data-testid="year-name-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} /></div>
              <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} /></div>
            </div>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_current} onChange={e => setForm({...form, is_current: e.target.checked})} /> Set as current academic year</label>
            <div className="border-t pt-4">
              <p className="font-bold text-sm mb-2">Terms</p>
              {form.terms.map((t, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-50 rounded p-2 mb-1 text-sm">
                  <span>{t.name}: {t.start_date} - {t.end_date}</span>
                  <Button variant="ghost" size="sm" onClick={() => setForm({...form, terms: form.terms.filter((_, j) => j !== i)})}>Remove</Button>
                </div>
              ))}
              <div className="grid grid-cols-3 gap-2 mt-2">
                <Input value={termForm.name} onChange={e => setTermForm({...termForm, name: e.target.value})} placeholder="Term name" />
                <Input type="date" value={termForm.start_date} onChange={e => setTermForm({...termForm, start_date: e.target.value})} />
                <div className="flex gap-1"><Input type="date" value={termForm.end_date} onChange={e => setTermForm({...termForm, end_date: e.target.value})} /><Button variant="outline" onClick={addTerm}>+</Button></div>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button><Button onClick={handleSave} className="bg-[#1B2A4A]" data-testid="save-year-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
